import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { useToast } from "../lib/toast";
import { Button, Field } from "../components/ui";
import { AuthAside } from "../components/AuthAside";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const toast = useToast();

  // Long enough for the closing halves to finish (see .signin in index.css).
  // Skipped entirely when the browser asks for reduced motion.
  const SIGNIN_EXIT_MS = 520;

  const handleLogin = async (e) => {
    e?.preventDefault();

    // Inline validation beats a toast here — the problem is on the field.
    const next = {};
    if (!email.trim()) next.email = "Enter your email address.";
    else if (!/^\S+@\S+\.\S+$/.test(email.trim()))
      next.email = "That doesn't look like a valid email.";
    if (!password) next.password = "Enter your password.";
    setErrors(next);
    if (Object.keys(next).length) return;

    try {
      setLoading(true);
      const res = await api.post("/api/auth/login", {
        email: email.trim(),
        password,
      });
      localStorage.setItem("token", res.data.token);

      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      if (reduceMotion) {
        navigate("/dashboard");
        return;
      }
      // Play the exit, then navigate. Loading stays true so the form can't be
      // resubmitted while it animates.
      setLeaving(true);
      setTimeout(() => navigate("/dashboard"), SIGNIN_EXIT_MS);
      return;
    } catch (err) {
      const msg = errMsg(err, "Invalid email or password.");
      setErrors({ password: msg });
      toast.error("Sign in failed", msg);
      setLoading(false);
    }
  };

  return (
    <div
      className={`signin flex min-h-screen bg-white${leaving ? " is-leaving" : ""}`}
    >
      <AuthAside />

      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px] animate-slide-up">
          <div className="mb-10 flex items-center gap-2.5 lg:hidden">
            <img src="/mmc_logo.png" alt="MMC Convert" className="h-10" />
            <span className="text-[15px] font-semibold text-ink-900">
              MMC Convert
            </span>
          </div>

          <h1 className="text-[28px] font-semibold tracking-tight text-ink-900">
            Welcome back
          </h1>
          <p className="mt-2 text-[14px] text-ink-500">
            Sign in to continue to your workspace.
          </p>

          <form onSubmit={handleLogin} className="mt-9 space-y-5" noValidate>
            <Field label="Email address" error={errors.email}>
              <input
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="you@company.com"
                className="field-input"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors((p) => ({ ...p, email: null }));
                }}
              />
            </Field>

            <Field label="Password" error={errors.password}>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className="field-input pr-16"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password)
                      setErrors((p) => ({ ...p, password: null }));
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-medium text-ink-500 transition hover:text-brand-600"
                  tabIndex={-1}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </Field>

            <Button type="submit" size="lg" loading={loading} className="w-full">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-7 text-center text-[13.5px] text-ink-500">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="font-semibold text-brand-600 transition hover:text-brand-700"
            >
              Create one
            </Link>
          </p>

          <p className="mt-12 text-center text-[11.5px] text-ink-400 lg:hidden">
            © 2026 MMC Convert
          </p>
        </div>
      </div>
    </div>
  );
}
